import clsx from 'clsx';
import React from 'react';
import { Link, To } from 'react-router-dom';

import { generateSearchParam } from '@cms/utils/helpers';
import { AssetsPath, EventsPath, ReportsPath, Routes, TagsPath, TemplatesPath, UsersPath } from '@cms/utils/routes';
import { useAppSelector } from '@cms/utils/store/hooks';
import { checkRouteRequiredRole, TEMPLATES } from '@dl/declarations';
import { AssetManagerIcon, CollectionsIcon, ComposerIcon, HelpIcon, PeopleIcon, RelatedTopicsIcon, TemplatesIcon, UserIcon } from '../icons';

type TNavbarItem = {
    id: number;
    title: string;
    to?: To;
    skipPermissionsCheck?: boolean;
    icon: {
        component: React.ReactNode
        className?: string;
    };

    links: Array<{
        id: number;
        title: string;
        to: To;
        skipPermissionsCheck?: boolean;
    }>
}

interface NavigationMenuProps {
    handleCloseNavbar?: () => void;
}

export const NavigationMenu: React.FC<NavigationMenuProps> = ({ handleCloseNavbar }) => {
    const { profile } = useAppSelector((state) => state.user)

    const checkHasPermissions = (link?: To, skip = false) => {
        if (skip) return true;
        if (!link) return true;

        const pathname = typeof link === 'string' ? link : link.pathname || '';
        const usedPathname = pathname.split('/').pop() || ''

        if (!usedPathname) return true;

        const search = typeof link === 'string' ? '' : link.search || '';
        const action = search.includes('mode=create') ? 'c' : search.includes('mode=update') ? 'u' : 'r';

        return checkRouteRequiredRole(usedPathname, action, profile.permissions);
    }

    const renderNavbarItem = (item: TNavbarItem) => {
        if (!checkHasPermissions(item.to, item.skipPermissionsCheck)) {
            return null
        }

        const hasAllowedLinks = item.links.some((link) => checkHasPermissions(link.to, link.skipPermissionsCheck));

        if (!hasAllowedLinks) {
            return null;
        }

        return (
            <div
                className={clsx(
                    "flex flex-col items-start gap-2",
                )}
            >
                {/* <div
                    className={clsx(
                        "w-14 h-14 flex justify-center items-center rounded-full",
                        item.icon.className
                    )}
                >
                    {item.icon.component}
                </div> */}

                <h3 className="text-xl text-cs-500">
                    {item.to ? <Link to={item.to} onClick={handleCloseNavbar}>{item.title}</Link> : item.title}
                </h3>

                {item.links.map((link) => {
                    if (!checkHasPermissions(link.to, link.skipPermissionsCheck)) {
                        return null
                    }

                    return (
                        <Link
                            key={link.id}
                            to={link.to}
                            className={clsx(
                                "text-cs-900 hover:text-cs-500 dark:text-white",
                            )}
                            onClick={handleCloseNavbar}
                        >
                            {link.title}
                        </Link>
                    )
                })}
            </div>
        )
    }

    return (
        <div
            className={clsx(
                "w-full flex flex-col sm:grid sm:grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-8",
                "px-8 md:px-16 py-4 md:py-8"
            )}
        >
            {renderNavbarItem({
                id: 1,
                title: 'Composer',
                to: { pathname: Routes.STORIES },
                icon: {
                    component: <ComposerIcon />,
                    className: 'bg-cs-900'
                },
                links: [
                    { id: 1, title: 'Create DL News article', to: { pathname: Routes.EDITOR, search: generateSearchParam(TemplatesPath, "id", TEMPLATES.ARTICLES.ID) } },
                    { id: 2, title: 'Create DL Research article', to: { pathname: Routes.EDITOR, search: generateSearchParam(TemplatesPath, "id", TEMPLATES.RESEARCH.ID) } },
                    { id: 3, title: 'Search articles', to: { pathname: Routes.STORIES } },
                ]
            })}

            {renderNavbarItem({
                id: 2,
                title: 'Templates',
                to: { pathname: Routes.TEMPLATES },
                icon: {
                    component: <TemplatesIcon />,
                    className: 'bg-cs-900'
                },
                links: [
                    { id: 1, title: 'Create template', to: { pathname: Routes.TEMPLATES, search: `${generateSearchParam(TemplatesPath, "mode", "create")}` } },
                    { id: 2, title: 'Search templates', to: { pathname: Routes.TEMPLATES } },
                ]
            })}

            {renderNavbarItem({
                id: 3,
                title: 'Collections',
                to: { pathname: Routes.COLLECTIONS },
                icon: {
                    component: <CollectionsIcon />,
                    className: 'bg-cs-900'
                },
                links: [
                    // { id: 1, title: 'Create collection', to: { pathname: Routes.COLLECTIONS, search: `${generateSearchParam(CollectionsPath, "mode", "create")}` } },
                    { id: 2, title: 'Search collections', to: { pathname: Routes.COLLECTIONS } },
                ]
            })}

            {renderNavbarItem({
                id: 4,
                title: 'Events',
                to: { pathname: Routes.EVENTS },
                icon: {
                    component: null,
                    className: 'bg-cs-900'
                },
                links: [
                    { id: 1, title: 'Create event', to: { pathname: Routes.EVENTS, search: `${generateSearchParam(EventsPath, "mode", "create")}` } },
                    { id: 2, title: 'Search events', to: { pathname: Routes.EVENTS } },
                ]
            })}

            {renderNavbarItem({
                id: 5,
                title: 'People',
                to: { pathname: Routes.USERS },
                icon: {
                    component: <PeopleIcon />,
                    className: 'bg-cs-500'
                },
                links: [
                    { id: 1, title: 'New member', to: { pathname: Routes.USERS, search: `${generateSearchParam(UsersPath, "mode", "create")}` } },
                    // { id: 2, title: 'Search team members', to: { pathname: Routes.USERS } },
                    { id: 3, title: 'Job openings', to: { pathname: Routes.JOBS } },
                    { id: 4, title: 'Authorization roles', to: { pathname: Routes.ROLES } },
                ]
            })}

            {renderNavbarItem({
                id: 6,
                title: 'Assets manager',
                to: { pathname: Routes.ASSETS },
                icon: {
                    component: <AssetManagerIcon />,
                    className: 'bg-cs-900'
                },
                links: [
                    { id: 1, title: 'New asset', to: { pathname: Routes.ASSETS, search: `${generateSearchParam(AssetsPath, "mode", "create")}` } },
                    { id: 2, title: 'Search assets', to: { pathname: Routes.ASSETS } },
                ]
            })}

            {renderNavbarItem({
                id: 7,
                title: 'Reports (pdf)',
                to: { pathname: Routes.REPORTS },
                icon: {
                    component: null,
                    className: 'bg-cs-900'
                },
                links: [
                    { id: 1, title: 'Create report', to: { pathname: Routes.REPORTS, search: `${generateSearchParam(ReportsPath, "mode", "create")}` } },
                    { id: 2, title: 'Search reports', to: { pathname: Routes.REPORTS } },
                ]
            })}

            {renderNavbarItem({
                id: 8,
                title: 'Related topics',
                to: { pathname: Routes.TAGS },
                icon: {
                    component: <RelatedTopicsIcon />,
                    className: 'bg-cs-900'
                },
                links: [
                    { id: 1, title: 'Add topic', to: { pathname: Routes.TAGS, search: `${generateSearchParam(TagsPath, "mode", "create")}` } },
                    // { id: 2, title: 'Search topics', to: { pathname: Routes.TAGS } },
                ]
            })}

            {renderNavbarItem({
                id: 9,
                title: 'User',
                icon: {
                    component: <UserIcon />,
                    className: 'bg-cs-500'
                },
                links: [
                    // { id: 1, title: 'Settings', to: { pathname: '/' }, skipPermissionsCheck: true },
                    { id: 2, title: 'Logout', to: { pathname: Routes.SIGN_OUT }, skipPermissionsCheck: true },
                    { id: 3, title: profile.isPasswordSet ? 'Change password' : 'Set password', to: { pathname: Routes.SET_PASSWORD }, skipPermissionsCheck: true },
                ]
            })}

            {renderNavbarItem({
                id: 10,
                title: 'Miscellaneous',
                icon: {
                    component: <HelpIcon />,
                    className: 'bg-[#654321]'
                },
                links: [
                    { id: 1, title: 'Configurations', to: { pathname: Routes.CONFIGURATIONS } },
                    { id: 2, title: 'Tools', to: { pathname: Routes.TOOLS } },
                    // { id: 3, title: 'Suggestions', to: { pathname: '/' } },
                    { id: 4, title: 'FAQ', to: { pathname: Routes.HELP } },
                ]
            })}
        </div>
    )
}
